<template>
    <b-form name="domainForm">
        <!-- Domain name -->
        <x-input
            :text="labelDomainName"
            :value="domain.name"
            @input="$_onInputName"
            :id="'domain-name'"
            :name="'domain-name'"
            :invalidMessage="nameMessage"
            :state="validName"
            :placeholder="placeholders.name"
            :maxlength="50"
            :required="isDomainNameRequired"
            :autofocus="true"
            :disabled="isGenerating"
        >
        </x-input>
        <div class="generate-domain-panel">
            <!-- Generate domain values -->
            <x-checkbox
                :id="'generate-domain-name'"
                :name="'generate-domain-name'"
                :text="labelDomainGenerate"
                :value="domain.isGenerated"
                @input="$_onGenerateDomain"
                :disabled="isGenerating"
            >
            </x-checkbox>
        </div>
        <!-- Genereren -->
        <b-form-group v-show="domain.isGenerated" :disabled="isGenerating">
            <b-card
                id="domainGenerationInputPanel"
                no-body
                header-tag="header"
                footer-tag="footer"
            >
                <template v-slot:header>
                    <h4 class="mb-0">{{ $t('domainAutofill') }}</h4>
                </template>
                <b-card-body>
                    <b-form-group class="mb-0">
                        <!-- Domain generation: OWS list -->
                        <!-- View generated domain OWS -->
                        <div v-show="!editDomainSettings">
                            <x-input
                                :text="$t('labelOws')"
                                :value="getOwsName"
                                :id="'domain-ows-name-view'"
                                :name="'domain-ows-name-view'"
                                plaintext
                            >
                            </x-input>
                        </div>
                        <!-- Edit generated domain OWS -->
                        <div v-show="editDomainSettings">
                            <x-select
                                :id="'domain-ows-name-edit'"
                                :name="'domain-ows-name-edit'"
                                :text="$t('labelOws')"
                                :value="getOwsName"
                                :options="getOwsServicesOptions"
                                @selected="$_onOwsServiceSelected"
                            >
                            </x-select>
                        </div>
                        <!-- Domain generation: Layer list -->
                        <!-- View generated domain OWS layer -->
                        <div v-show="showLayerInput && !editDomainSettings">
                            <x-input
                                :text="$t('labelLayer')"
                                :value="domain.generateLayerName"
                                :id="'domain-layer-name-view'"
                                :name="'domain-layer-name-view'"
                                plaintext
                            >
                            </x-input>
                        </div>
                        <!-- Edit generated domain layer -->
                        <div v-show="showLayerInput && editDomainSettings">
                            <x-select
                                :id="'domain-layer-name-edit'"
                                :name="'domain-layer-name-edit'"
                                labelClass="prewrap"
                                :text="
                                    isOwsNedGlobeServices
                                        ? `${$t('tableResults.tableName')}`
                                        : `${$t('labelLayer')}`
                                "
                                :value="getLayerName"
                                :options="getLayerOptions"
                                @selected="$_onLayerSelected"
                            >
                            </x-select>
                        </div>
                        <!-- Domain generation: Attribute list -->
                        <!-- View generated domain attribute -->
                        <div v-show="showAttributeInput && !editDomainSettings">
                            <x-input
                                :text="labelAttributeName"
                                :value="domain.generateAttribute"
                                :id="'domain-attribute-name-view'"
                                :name="'domain-attribute-name-view'"
                                plaintext
                            >
                            </x-input>
                        </div>
                        <!-- Edit generated domain attribute-->
                        <div v-show="showAttributeInput && editDomainSettings">
                            <x-select
                                :id="'domain-attribute-name-edit'"
                                :name="'domain-attribute-name-edit'"
                                :text="labelAttributeName"
                                :value="getAttributeName"
                                :options="getAttributeOptions"
                                @selected="$_onAttributeSelected"
                            >
                            </x-select>
                        </div>
                        <!-- Generate domain: buttons -->
                        <div class="btn-group">
                            <!-- Fill domainvalues-->
                            <b-button
                                id="domain-generate"
                                variant="primary"
                                @click="
                                    isOwsNedGlobeServices
                                        ? $_generateDomainValuesTableAttribute()
                                        : $_generateDomainValues()
                                "
                                :disabled="isGenerating"
                            >
                                {{ $t('labelButtonGenerate') }}
                                <span
                                    class="fas"
                                    v-bind:class="{
                                        'fa-check': !isGenerating,
                                        'fa-sync-alt fa-spin': isGenerating
                                    }"
                                >
                                </span>
                            </b-button>
                            <!-- Change domain generation values -->
                            <b-button
                                id="domain-change"
                                @click="$_changeDomainSettings"
                                :disabled="isGenerating || editDomainSettings"
                            >
                                {{ $t('labelButtonModify') }}
                            </b-button>
                        </div>
                    </b-form-group>
                </b-card-body>
                <template v-slot:footer>
                    <span v-show="isGenerating"
                        >Status: Bezig met vullen domein...</span
                    >
                    <span v-show="!isGenerating"
                        >Status: Klaar met vullen van domein.</span
                    >
                </template>
            </b-card>
        </b-form-group>
        <b-form-group :disabled="isGenerating">
            <!-- Deze niet tonen wanneer domain.generated? Of inhoud tonen? Standaard selectie mogelijk maken? -->
            <label class="font-weight-bold">{{
                $t('labelDomainValues')
            }}</label>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                :tbody-tr-class="$_attributeTableRowClass"
                :items="domainValues"
                :fields="fields"
            >
                <template v-slot:cell(remove)="row">
                    <b-nav
                        size="sm"
                        @click="
                            !isGenerating && $_onDeleteKeyValue(row.item.id)
                        "
                        class="delete"
                    >
                        <em class="fas fa-times"></em>
                    </b-nav>
                </template>
            </b-table>
            <b-row v-show="showManualAdd" class="flexible-row">
                <b-col class="fixed-col">
                    <!-- Key -->
                    <x-input
                        :text="labelDomainKey"
                        v-model="newKeyValue.key"
                        :id="'new-domain-key'"
                        :name="'new-domain-key'"
                        :placeholder="placeholders.key"
                        :maxlength="150"
                        :required="
                            newKeyValue.key.length === 0 &&
                            newKeyValue.value.length > 0
                        "
                        @input="$_onInputDomainKey"
                        :invalidMessage="newValueErrorMessage"
                        :state="
                            newKeyInvalidMessage.length === 0 &&
                            newKeyValidState
                        "
                    >
                    </x-input>
                </b-col>
                <b-col class="flexible-col">
                    <div class="flexible-row">
                        <!-- Value -->
                        <x-input
                            class="flexible-col"
                            :text="labelDomainValue"
                            v-model="newKeyValue.value"
                            :id="'new-domain-value'"
                            :name="'new-domain-value'"
                            :placeholder="placeholders.value"
                            :maxlength="150"
                            :state="newValueInvalidMessage.length === 0"
                            :invalidMessage="newValueInvalidMessage"
                            :required="
                                newKeyValue.key.length > 0 &&
                                newKeyValue.value.length === 0
                            "
                        >
                        </x-input>
                        <div class="button-container">
                            <!-- Add button -->
                            <b-button
                                id="add-keyvalue"
                                variant="primary"
                                @click="$_onAddNewKeyValue(true)"
                                :disabled="
                                    !newKeyValidState || emptyValueExists
                                "
                                >{{ $t('btnAddLabel') }}</b-button
                            >
                        </div>
                    </div>
                </b-col>
            </b-row>
            <span v-show="noKeyvaluesErrorMessage" class="text-danger">{{
                noKeyvaluesErrorMessage
            }}</span>
        </b-form-group>
    </b-form>
</template>

<script>
    import {
        arraySortAscendingFunction,
        nprogress,
        convertToValueTextPair
    } from '../../services/utilities';
    import {
        MODE_ADD,
        MODE_EDIT,
        WEBJOB_STATUS,
        NEDGLOBE_SERVICES
    } from '../../constants';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';
    import { orderBy } from 'lodash';

    export default {
        name: 'DomainDetailForm',

        mixins: [modalMixin, refcodeMixin],

        components: {
            'x-input': XInput,
            'x-select': XSelect,
            'x-checkbox': XCheckbox
        },

        props: {
            displayedInModal: { type: Boolean, required: false, default: false },
            tableResultSearchAttribute: {
                type: Object,
                required: false,
                default: null
            }
        },

        data() {
            return {
                // domain object
                domain: {},
                newKeyValidState: true,
                noKeyvaluesErrorMessage: null,
                // OWS services array
                owsServices: [],
                // OWS services layers array
                owsServiceLayers: [],
                // OWS services attributes array
                owsLayerAttributes: [],
                // selected OWS
                ows: null,
                // selected Layer
                layer: null,
                // selected Attribute
                attribute: null,

                // NedGlobeServices tables
                tableNames: [],
                tableAttributes: [],

                newId: -1,
                newKeyValue: {
                    key: '',
                    value: ''
                },

                webJobStatuses: [],

                status: { description: '...', code: '...' },

                // domain generation in progress
                isGenerating: false,

                dataLoaded: false,

                editDomainSettings: false,

                showFormInModal: false,

                validName: true,
                nameMessage: '',
                showManualAdd: true,
                domainGenerateFailedTitle: this.$t('domainGenerateFailedTitle'),
                labelDomainName: this.$t('labelDomainName'),
                labelDomainGenerate: this.$t('labelDomainGenerate'),
                labelDomainKey: this.$t('labelDomainKey'),
                labelDomainValue: this.$t('labelDomainValue'),
                labelAttributeName: this.$t('labelAttributeName'),
                newKeyInvalidMessage: '',
                newValueInvalidMessage: '',

                // placeholders list
                placeholders: {
                    name: '',
                    key: '',
                    value: ''
                },
                // table fields
                fields: [
                    {
                        key: 'key',
                        label: this.$t('labelDomainKey'),
                        sortable: false,
                        thStyle: {
                            border: 0,
                            width: '250px'
                        }
                    },
                    {
                        key: 'text',
                        label: this.$t('labelDomainValue'),
                        sortable: false,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'remove',
                        label: '',
                        sortable: false,
                        thStyle: {
                            border: 0,
                            width: '95px'
                        }
                    }
                ]
            };
        },

        computed: {
            /**
             * domain values array for b-table component
             */
            domainValues() {
                if (this.domain?.keyvalues) {
                    return this.domain.keyvalues
                        .map((item) => {
                            return {
                                id: item.id,
                                key: item.key,
                                text: item.value
                            };
                        })
                        .sort(arraySortAscendingFunction('text'));
                }
                return [];
            },

            /**
             * Is a domain name required?
             * @return {Boolean} true if required, false otherwise
             */
            isDomainNameRequired() {
                return !this.displayedInModal || this.showFormInModal;
            },

            /**
             * Get a OWS services list for an options field
             * @return {array} array with objects
             */
            getOwsServicesOptions() {
                // Show only the OWS with valid WFS URL
                const owsServicesWithUriWFS = this.owsServices.filter((service) => {
                    return service.uri_WFS && service.uri_WFS.length > 0;
                });
                return owsServicesWithUriWFS.map((item) => {
                    return {
                        id: item.id,
                        // fields for x-select (b-form-select)
                        value: item.name,
                        text: item.name
                    };
                });
            },

            /**
             * Get a Layer list for an options field
             * @return {array} array with objects
             */
            getLayerOptions() {
                if (this.isOwsNedGlobeServices) {
                    return convertToValueTextPair(
                        this.tableNames.map(({ layerName }) => layerName)
                    );
                } else {
                    const layerOptions = this.owsServiceLayers.map((item) => {
                        return {
                            // fields for x-select (b-form-select)
                            value: item.layerName,
                            text: item.layerName
                        };
                    });
                    return orderBy(layerOptions, [
                        (element) => element.text.toLowerCase()
                    ]);
                }
            },

            /**
             * Get a Attributes list for an options field
             * @return {array} array with objects
             */
            getAttributeOptions() {
                if (this.isOwsNedGlobeServices) {
                    return convertToValueTextPair(
                        this.tableAttributes,
                        'name',
                        'name'
                    ).sort(arraySortAscendingFunction('text'));
                } else {
                    const attributeOptions = this.owsLayerAttributes.map((item) => {
                        return {
                            // fields for x-select (b-form-select)
                            value: item.name,
                            text: item.name
                        };
                    });
                    return orderBy(attributeOptions, [
                        (element) => element.text.toLowerCase()
                    ]);
                }
            },

            /**
             * Get the OWS id property
             * @return {string} Name of the OWS or ''
             */
            getOwsId() {
                if (this.ows) {
                    return `${this.ows.id}`;
                }
                return '-1';
            },

            /**
             * Get the OWS name property
             * @return {string} Name of the OWS or null
             */
            getOwsName() {
                return this.ows?.name;
            },

            /**
             * Get the layer name
             * @return {string} Name of the layer or null
             */
            getLayerName() {
                return this.layer?.layerName;
            },

            /**
             * Get the name of the current attribute
             * @return {string} Name of the attribute or null
             */
            getAttributeName() {
                return this.attribute?.name;
            },

            newValueErrorMessage() {
                return this.newKeyValidState === false
                    ? this.$t('domainKeyAlreadyExistsMessage')
                    : this.newKeyInvalidMessage;
            },

            /**
             * Is the layer input control visible?
             * @return {boolean} true if the control is visible, false otherwise
             */
            showLayerInput() {
                return !!this.ows;
            },

            /**
             * Is the attribute input control visible?
             * @return {boolean} true if the control is visible, false otherwise
             */
            showAttributeInput() {
                return this.ows && this.layer;
            },

            isOwsNedGlobeServices() {
                return this.getOwsName === NEDGLOBE_SERVICES;
            },

            emptyValueExists() {
                if (
                    this.domain?.keyvalues &&
                    this.newKeyValue.key.trim().length === 0
                ) {
                    return this.domain.keyvalues.some((kv) => kv.key === '');
                }
                return false;
            }
        },

        methods: {
            async initialize(id) {
                this.id = id;
                if (this.id > 0) {
                    nprogress.start();
                    this.mode = MODE_EDIT;
                    // get existing data from the backend
                    await this.$store
                        .dispatch('useApiData', {
                            name: 'GetFeedbackDomain',
                            parameters: { id: this.id }
                        })
                        .then((result) => {
                            this.domain = Object.assign({}, result.data);
                            if (!this.domain.keyvalues) {
                                this.domain.keyvalues = [];
                            }
                            // load OWS list
                            this.$_loadOwsServices().then(() => {
                                this.ows = this.$_getOwsService(
                                    this.domain.generateOwsId
                                );
                                this.dataLoaded = true;
                                // start de get messages loop
                                this.$_getGenerateStatus();
                            });
                            // domain loaded, notify parent unavailable flag
                            this.$emit('unavailable', this.domain.unavailable);
                            nprogress.done();
                        });
                } else {
                    // new form
                    this.mode = MODE_ADD;
                    // empty domain object
                    this.domain = {
                        name: '',
                        keyvalues: [],
                        isGenerated: this.tableResultSearchAttribute ? true : false
                    };
                    // load OWS list
                    this.$_loadOwsServices().then(() => {
                        // if started from table results search attribute, then use NEDGLOBE_SERVICES ows
                        if (this.tableResultSearchAttribute) {
                            this.ows = this.$_getOwsService(NEDGLOBE_SERVICES);
                        }
                        this.dataLoaded = true;
                        // start de get messages loop
                        this.$_getGenerateStatus();
                    });
                }
            },

            /**
             * CSS class name of a table row
             * @param  {[type]} item [description]
             * @param  {[type]} type [description]
             */
            $_attributeTableRowClass() {
                return 'tablerow text-break';
            },
            $_domainKeyExists() {
                const newDomainKey = this.newKeyValue.key.trim();
                return this.domain.keyvalues.some((kv) => kv.key === newDomainKey);
            },

            /**
             * Checks if the given domainname already exists in the database
             * @return {void} - Sets error message if needed
             */
            $_domainNameExists() {
                const domainParams = {
                    id: this.id,
                    name: this.domain.name
                };
                this.$store
                    .dispatch('useApiData', {
                        name: 'DomainExists',
                        data: domainParams
                    })
                    .then((result) => {
                        if (result.data) {
                            this.validName = false;
                            this.nameMessage = 'Deze domein naam is al in gebruik';
                        } else {
                            this.validName = true;
                            this.nameMessage = '';
                        }
                    })
                    .catch((e) => {
                        this.showToast({
                            title: 'Interne fout',
                            message: e.message,
                            variant: 'danger',
                            autoHideDelay: 5000
                        });
                    });
            },
            /**
             * Load OWS services from the API
             * @return {*} promise object
             */
            $_loadOwsServices() {
                return new Promise((resolve, reject) => {
                    this.$store
                        .dispatch('useApiData', {
                            name: 'GetOwsOverview'
                        })
                        .then(
                            (result) => {
                                if (result.data && result.data.length > 0) {
                                    this.owsServices = result.data;
                                }
                                resolve(true);
                            },
                            (error) => {
                                this.$_getdomainkeyvaluesFailure(error.message);
                                resolve(false);
                            }
                        );
                });
            },

            /**
             * Load layers from the API
             * @return {*} promise object
             */
            $_loadLayers() {
                return new Promise((resolve, reject) => {
                    if (this.ows) {
                        // get OWS layers from the API
                        this.$store
                            .dispatch('useApiData', {
                                name: 'GetIncludeLayers',
                                parameters: { id: this.ows.id }
                            })
                            .then(
                                (result) => {
                                    if (
                                        result.data &&
                                        result.data.layers &&
                                        result.data.layers.length > 0
                                    ) {
                                        // list with OWS service layers
                                        // PBI 171165, Task 17500: Voor domein automatisch vullen zijn alleen WFS lagen van belang.
                                        this.owsServiceLayers =
                                            result.data.layers.filter(
                                                (layer) =>
                                                    layer.serviceType.toUpperCase() ===
                                                    'WFS'
                                            );
                                        resolve(true);
                                    } else {
                                        this.showToast({
                                            title: 'Geen lagen beschikbaar',
                                            message:
                                                'Er zijn geen lagen gevonden voor deze OWS service.',
                                            variant: 'danger',
                                            autoHideDelay: 5000
                                        });
                                        resolve(true);
                                    }
                                },
                                (error) => {
                                    this.showToast({
                                        title: 'OWS lagen ophalen mislukt',
                                        message: `Het ophalen van de OWS lagen is mislukt vanwege een fout. De foutmelding was: ${error.message}.`,
                                        variant: 'danger',
                                        autoHideDelay: 5000
                                    });
                                    resolve(false);
                                }
                            );
                    } else {
                        resolve();
                    }
                });
            },

            /**
             * Load attributes for the selected layer
             * @return {*} Promise object
             */
            $_loadAttributes() {
                return new Promise((resolve, reject) => {
                    if (this.layer) {
                        // get layer attributes from the API
                        this.$store
                            .dispatch('useApiData', {
                                name: 'GetFeatureTypeInfoByName',
                                parameters: {
                                    layerName: this.layer.layerName,
                                    owsId: this.ows.id
                                }
                            })
                            .then(
                                (result) => {
                                    if (
                                        result.data &&
                                        result.data.attributes &&
                                        result.data.attributes.length > 0
                                    ) {
                                        this.owsLayerAttributes =
                                            result.data.attributes;
                                        this.layer.geometryColumn =
                                            result.data.geometryColumn;
                                        this.domain.generateLayerGeometryColumn =
                                            this.layer.geometryColumn;
                                        resolve(true);
                                    } else {
                                        this.layer.geometryColumn = null;
                                        this.domain.generateLayerGeometryColumn =
                                            null;

                                        this.showToast({
                                            title: 'Geen attributen beschikbaar',
                                            message:
                                                'Er zijn geen attributen gevonden voor de geselecteerde laag.',
                                            variant: 'danger',
                                            autoHideDelay: 5000
                                        });
                                        resolve(true);
                                    }
                                },
                                (error) => {
                                    this.showToast({
                                        title: 'Attributen ophalen mislukt',
                                        message: `Het ophalen van de attributen is mislukt vanwege een fout. De foutmelding was: ${error.message}.`,
                                        variant: 'danger',
                                        autoHideDelay: 5000
                                    });
                                    resolve(false);
                                }
                            );
                    } else {
                        // no layer selected
                        resolve();
                    }
                });
            },
            $_onInputDomainKey() {
                this.$_setDomainValue();
                // Update newKeyValidState
                this.newKeyValidState = !this.$_domainKeyExists();
            },
            /**
             * Name input event handler
             * @param  {string} value domain name
             */
            $_onInputName(value) {
                this.domain.name = value;
                this.$_domainNameExists();
            },

            /**
             * Checkbox toggle handler
             * @param  {[type]} value [description]
             * @return {[type]}       [description]
             */
            $_onGenerateDomain(value) {
                this.noKeyvaluesErrorMessage = null;
                this.domain.isGenerated = value;
                this.editDomainSettings = false;
                if (
                    this.domain.isGenerated &&
                    (this.mode === MODE_ADD ||
                        !(
                            this.domain.generateOwsId &&
                            this.domain.generateAttribute &&
                            this.domain.generateLayerName
                        ))
                ) {
                    this.editDomainSettings = true;
                }
            },

            $_onDeleteKeyValue(value) {
                this.domain.keyvalues = this.domain.keyvalues.filter((item) => {
                    return item.id !== value;
                });
            },

            /**
             * Add a new key-value pair to the list
             */
            $_onAddNewKeyValue(addEmptyValue = false) {
                const validNewKeyValue = this.$_validateNewKeyValue();
                if (validNewKeyValue) {
                    this.noKeyvaluesErrorMessage = null;
                    let newDomainKey = this.newKeyValue.key.trim();
                    let newDomainValue = this.newKeyValue.value.trim();

                    // Add special item to the list for null or empty values
                    if (
                        addEmptyValue &&
                        newDomainKey.length === 0 &&
                        newDomainValue.length === 0
                    ) {
                        newDomainKey = '';
                        newDomainValue = this.$t('emptyValue');
                    }
                    if (
                        addEmptyValue ||
                        (newDomainKey.length > 0 && newDomainValue.length > 0)
                    ) {
                        // Create a unique id
                        this.newId = this.newId - 1;
                        this.domain.keyvalues.push({
                            id: this.newId,
                            key: newDomainKey,
                            value: newDomainValue
                        });
                        // Reset input values
                        this.newKeyValue.key = '';
                        this.newKeyValue.value = '';
                    }
                }
                return validNewKeyValue;
            },
            /**
             * An OWS services is selected from the list
             */
            async $_onOwsServiceSelected(value) {
                this.ows = null;
                if (value) {
                    this.ows = this.$_getOwsService(value);
                    if (this.ows) {
                        this.domain.generateOwsId = this.ows.id;

                        if (this.isOwsNedGlobeServices) {
                            this.tableNames = await this.$store.dispatch(
                                'getTableNames'
                            );
                            // set layer
                            // if started from table results search attribute, then use given layerName
                            this.layer = this.$_getLayer(
                                this.tableResultSearchAttribute
                                    ? this.tableResultSearchAttribute.layerName
                                    : this.domain.generateLayerName
                            );
                        } else {
                            // load layers for selected OWS
                            this.$_loadLayers().then(() => {
                                // set layer
                                this.layer = this.$_getLayer(
                                    this.domain.generateLayerName
                                );
                            });
                        }
                    } else {
                        this.domain.generateOwsId = null;
                    }
                }
            },

            /**
             * A Layer/Table is selected from the list
             */
            async $_onLayerSelected(value) {
                this.layer = null;
                if (value) {
                    this.layer = this.$_getLayer(value);
                    if (this.layer) {
                        this.domain.generateLayerName = this.layer.layerName;

                        if (this.isOwsNedGlobeServices) {
                            await this.$_loadTableAttributes(value);
                            // set attribute
                            // if started from table results search attribute, then use given columnName
                            this.attribute = this.$_getAttribute(
                                this.tableResultSearchAttribute
                                    ? this.tableResultSearchAttribute.columnName
                                    : this.domain.generateAttribute
                            );
                        } else {
                            // load attributes for the selected layer
                            this.$_loadAttributes().then(() => {
                                // set attribute
                                this.attribute = this.$_getAttribute(
                                    this.domain.generateAttribute
                                );
                            });
                        }
                    } else {
                        this.domain.generateLayerName = null;
                    }
                }
            },

            /**
             * An attribute is selected from the list
             */
            $_onAttributeSelected(value) {
                this.attribute = null;
                if (value) {
                    this.attribute = this.$_getAttribute(value);
                    if (this.attribute) {
                        this.domain.generateAttribute = this.attribute.name;
                    } else {
                        this.domain.generateAttribute = null;
                    }
                }
            },
            /**
             * Get status of the webjobs
             * @return {*}  Promise object
             */
            $_getWebJobStatuses() {
                return new Promise((resolve, reject) => {
                    // retrieve refCode list once
                    if (this.webJobStatuses.length === 0) {
                        this.getRefCodes(WEBJOB_STATUS.KEY)
                            .then((result) => {
                                this.webJobStatuses = result.data;
                                resolve(this.webjobStatuses);
                            })
                            .catch((error) => {
                                reject(error.message);
                            });
                    } else {
                        // Return existing list when already known
                        resolve(this.webJobStatuses);
                    }
                });
            },

            /**
             * Generate domain values
             */
            $_generateDomainValues() {
                // Stop generating domain if not all details known
                this.$_validateDomainName(this.domain);
                const isValid = this.$_validateDomainGenerationDetails(this.domain);
                if (!isValid) {
                    this.showToast({
                        title: this.domainGenerateFailedTitle,
                        message: this.$t('errorRequiredFieldsUnset'),
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                    return;
                }

                this.$_getWebJobStatuses().then(
                    () => {
                        this.status = this.webJobStatuses.find(
                            (o) => o.code === WEBJOB_STATUS.REQUEST_SENT_PLEASE_WAIT
                        );
                        if (this.status) {
                            this.domain.status = this.status.code;
                            // set saving state
                            this.isGenerating = true;
                            this.$root.$emit('domain-generating', {
                                isGenerating: this.isGenerating,
                                domain: this.domain
                            });
                            this.$store
                                .dispatch('useApiData', {
                                    name: 'GenerateDomainValues',
                                    data: this.domain
                                })
                                .then(
                                    (result) => {
                                        if (result.data.isQueued) {
                                            const message = this.displayedInModal
                                                ? 'Domein genereren is ingeroosterd. Klik op het kruisje om terug te gaan naar vorige venster.'
                                                : 'Domein genereren is ingeroosterd. U kunt via het menu naar een ander venster navigeren.';
                                            this.showToast({
                                                title: null,
                                                message: message,
                                                variant: 'success',
                                                autoHideDelay: 5000
                                            });
                                            this.domain.id = result.data.id;
                                            this.$root.$emit('generated-domain', {
                                                isGenerating: this.isGenerating,
                                                domain: this.domain
                                            });
                                            this.generateButtonEnabled = false;
                                            this.$_getGenerateStatus();
                                        }
                                    },
                                    (error) => {
                                        this.$_domainGenerateFailure(error.message);
                                    }
                                );
                        }
                    },
                    (error) => {
                        this.$_domainGenerateFailure(error.message);
                    }
                );
            },

            $_domainGenerateFailure(message) {
                this.showToast({
                    title: this.domainGenerateFailedTitle,
                    message: this.$t('domainGenerateFailedMessage', [`${message}`]),
                    variant: 'danger',
                    autoHideDelay: 5000
                });
            },

            /******  Domain status Websocket functions ******/

            /**
             * Houdt status bij met status voortgang uit de domain tabel
             */
            $_getGenerateStatus() {
                let url = this.$store.getters.endpoint.replace('https://', '');
                url = `${url}/domain/getdomainstatusbywebsocket`;
                const controleSocket = new WebSocket(`wss://${url}`);

                /**
                 * WebSocket on open event handler
                 * @param {object} event object
                 */
                controleSocket.onopen = (event) => {
                    this.$_updateControleSocketState(controleSocket);
                };

                /**
                 * WebSocket on message event handler
                 * @param {object} event object
                 */
                controleSocket.onmessage = (event) => {
                    // Read status from websocket/database
                    this.$_getWebJobStatuses().then(() => {
                        this.status = this.webJobStatuses.find(
                            (s) => s.code === event.data
                        );
                    });
                    // Check if proces was started and is now finished
                    if (this.$_isFinishedWithDomainGeneration()) {
                        // Enable generate domain button
                        this.generateButtonEnabled = true;
                        this.isGenerating = false;
                        this.$root.$emit('domain-generating', {
                            isGenerating: this.isGenerating,
                            domain: null
                        });
                        // Stop met controleren van de status
                        controleSocket.close();
                        // Update domain status to idle
                        this.$_updateDomainStatusToIdle();
                        // Show new keyvalues
                        this.$_refreshDomainKeyValues();
                    } else {
                        // vraag opnieuw de laatste status op
                        setTimeout(() => {
                            this.$_updateControleSocketState(controleSocket);
                        }, 250);
                    }
                };

                /**
                 * WebSocket on close event handler
                 */
                controleSocket.onclose = () => {
                    if (this.$_isWaitingForDomainGeneration()) {
                        return;
                    }
                };

                /**
                 * WebSocket on error event handler
                 */
                controleSocket.onerror = () => {
                    // herstart de get messages loopc
                    this.$_getGenerateStatus();
                };
            },

            async $_updateDomainStatusToIdle() {
                const domainParams = { id: this.domain.id };
                await this.$store
                    .dispatch('useApiData', {
                        name: 'SetDomainStatusIdle',
                        data: domainParams
                    })
                    .then(
                        (result) => {
                            // no handling
                        },
                        (error) => {
                            this.showToast({
                                title: 'Afronden domein genereren mislukt',
                                message: `Afronden domein genereren mislukt vanwege een fout. De foutmelding was: ${error.message}.`,
                                variant: 'danger',
                                autoHideDelay: 5000
                            });
                        }
                    );
            },

            $_updateControleSocketState(controleSocket) {
                if (controleSocket.readyState === controleSocket.OPEN) {
                    if (this.domain && this.domain.id) {
                        controleSocket.send(this.domain.id);
                    }
                }
            },

            /**
             * Check if the domain generation is finished, based on domain status
             * @returns {boolean} true if domain generation is finished
             */
            $_isFinishedWithDomainGeneration() {
                return !this.$_isWaitingForDomainGeneration();
            },

            /**
             * Check if the domain generation is ongoing, based on domain status
             * @returns {boolean} true if domain generation is finished still busy
             */
            $_isWaitingForDomainGeneration() {
                // Process done, finished message may be extended with 'more than x records' found messages check for includes
                if (this.status.code === WEBJOB_STATUS.DOMAIN_GENERATION_FINISHED) {
                    return false;
                }

                switch (this.status.code) {
                    // Fall through: process ongoing
                    case WEBJOB_STATUS.REQUEST_SENT_PLEASE_WAIT:
                    case WEBJOB_STATUS.DOMAIN_GENERATION_IN_PROGRESS:
                        return true;
                    // Fall through: process done
                    case WEBJOB_STATUS.NO_REQUEST_SENT:
                    case WEBJOB_STATUS.DOMAIN_GENERATION_ERROR:
                    case WEBJOB_STATUS.DOMAIN_GENERATION_FINISHED:
                    default:
                        return false;
                }
            },

            /**
             * Get new list of Key Values for the given domain id
             * Used for retrieved the updated KV list after triggering domain generation
             */
            $_refreshDomainKeyValues() {
                this.$store
                    .dispatch('useApiData', {
                        name: 'GetDomainKeyValues',
                        parameters: { id: this.domain.id }
                    })
                    .then((result) => {
                        this.domain.keyvalues = result.data;
                    })
                    .catch((error) => {
                        this.$_getdomainkeyvaluesFailure(error.message);
                        return false;
                    });
            },

            $_getdomainkeyvaluesFailure(message) {
                this.showToast({
                    title: 'OWS services ophalen mislukt',
                    message: `Het ophalen van de OWS services is mislukt vanwege een fout. De foutmelding was: ${message}.`,
                    variant: 'danger',
                    autoHideDelay: 5000
                });
            },

            /**
             * Change the domain generation input settings
             */
            $_changeDomainSettings() {
                this.editDomainSettings = true;
            },

            /**
             * Check if required domain details are present
             * @param {any} domain the domain to be generated
             * @return {boolean} - False if not all details are present
             */
            $_validateDomainGenerationDetails(domain) {
                // Return !! to return undefined values as false
                return !!(
                    domain.generateAttribute &&
                    domain.generateLayerName &&
                    domain.generateOwsId &&
                    domain.isGenerated &&
                    domain.name
                );
            },

            /**
             * Check if the domain is valid
             * @param {object} domain, parameters object
             * @return {boolean} - False if form is not valid, proces is cancelled
             */
            $_validateDomainDetails(domain) {
                if (domain.isGenerated) {
                    this.$_validateDomainName(domain);
                    const detailsValid =
                        this.$_validateDomainGenerationDetails(domain);
                    const valuesValid = this.$_validateDomainKeyvalues(domain);
                    return detailsValid && valuesValid;
                } else {
                    this.$_validateDomainKeyvalues(domain);
                    const domainNameAndKeyvaluesValid =
                        domain.name.length > 0 && domain.keyvalues.length > 0;
                    if (!domainNameAndKeyvaluesValid) {
                        this.$_validateDomainName(domain);
                    }
                    return domainNameAndKeyvaluesValid;
                }
            },

            /**
             * Validates the domain Keyvalues
             * @param domain
             */
            $_validateDomainKeyvalues(domain) {
                let validKeyvalues = true;
                if (domain && domain.keyvalues.length === 0) {
                    this.noKeyvaluesErrorMessage = this.$t(
                        'domainNoKeyvaluesErrorMessage'
                    );
                    validKeyvalues = false;
                }
                return validKeyvalues;
            },

            /**
             * Validates whether the domain name is filled in
             * @param domain
             */
            $_validateDomainName(domain) {
                this.validName = domain.name.length > 0;
                this.nameMessage = this.validName ? '' : this.$t('requiredField');
            },

            $_validateNewKeyValue() {
                let valid = false;
                this.newKeyInvalidMessage = '';
                this.newValueInvalidMessage = '';

                const newDomainKey = this.newKeyValue.key?.trim();
                const newDomainValue = this.newKeyValue.value?.trim();

                valid =
                    (newDomainKey.length > 0 && newDomainValue.length > 0) ||
                    (newDomainKey.length === 0 && newDomainValue.length === 0);
                if (!valid) {
                    this.newKeyInvalidMessage =
                        newDomainKey.length === 0 && newDomainValue.length > 0
                            ? 'Dit veld is verplicht'
                            : '';
                    this.newValueInvalidMessage =
                        newDomainKey.length > 0 && newDomainValue.length === 0
                            ? 'Dit veld is verplicht'
                            : '';
                }
                return valid;
            },

            /**
             * Get the OWS service with (id = owsIdOrName) or (name = owsIdOrName)
             * @param  {Number} owsIdOrName id or name of the OWS
             * @return {Object}       OWS object or null
             */
            $_getOwsService(owsIdOrName) {
                const filtered = this.owsServices.filter((item) => {
                    return item.id === owsIdOrName || item.name === owsIdOrName;
                });
                if (filtered && filtered.length > 0) {
                    return filtered[0];
                }
                return null;
            },

            /**
             * Get the Layer with layerName = name
             * @param  {Number} layerName   name of the layer
             * @return {Object}             Layer object or null
             */
            $_getLayer(name) {
                let filtered = [];
                if (this.isOwsNedGlobeServices) {
                    filtered = this.tableNames.filter((item) => {
                        return item.layerName === name;
                    });
                } else {
                    filtered = this.owsServiceLayers.filter((item) => {
                        return item.layerName === name;
                    });
                }
                if (filtered && filtered.length > 0) {
                    return filtered[0];
                }
                return null;
            },

            /**
             * Get the OWS service with Id = owsId
             * @param  {Number} owsId Id of the OWS
             * @return {Object}       Attribute object or null
             */
            $_getAttribute(name) {
                let filtered = [];
                if (this.isOwsNedGlobeServices) {
                    filtered = this.tableAttributes.filter((item) => {
                        return item.name === name;
                    });
                } else {
                    filtered = this.owsLayerAttributes.filter((item) => {
                        return item.name === name;
                    });
                }
                if (filtered && filtered.length > 0) {
                    return filtered[0];
                }
                return null;
            },

            /**
             * Place the alias input field after changes in the input field name
             * @param {string} domain_value name of the field item
             */
            $_setDomainValue: function () {
                if (
                    !this.newKeyValue.value ||
                    this.newKeyValue.alias === this.newKeyValue.value
                ) {
                    this.newKeyValue.value = this.newKeyValue.alias =
                        this.newKeyValue.key;
                } else {
                    this.newKeyValue.alias = this.newKeyValue.key;
                }
            },

            /**
             * save domain
             */
            save(self) {
                return new Promise(function (resolve, reject) {
                    // validation
                    const isValid = self.$_validateDomainDetails(self.domain);
                    if (!isValid) {
                        reject(self.$t('errorRequiredFieldsUnset'));
                    } else {
                        // continue save
                        self.domain.organizationId =
                            self.$store.getters.userInfo.organizationId;
                        // Set domain status code
                        self.domain.status =
                            self.domain.status && self.domain.status.code
                                ? self.domain.status.code
                                : WEBJOB_STATUS.NO_REQUEST_SENT;
                        // call api
                        self.$store
                            .dispatch('useApiData', {
                                name: 'SaveDomain',
                                data: self.domain
                            })
                            .then(
                                (result) => {
                                    // Resolve with object for the purpose of modal
                                    resolve(result);
                                },
                                (error) => {
                                    reject(`Foutmelding: ${error.response.data}.`);
                                }
                            );
                    }
                });
            },
            async $_loadTableAttributes(value) {
                if (value) {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetTableAttributes',
                        parameters: { TableName: value }
                    });
                    this.tableAttributes = result?.data?.filter(
                        ({ binding }) =>
                            binding === 'string' || binding === 'number'
                    );
                }
            },
            async $_generateDomainValuesTableAttribute() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetUniqueTableAttributeValues',
                    parameters: {
                        TableName: this.layer.layerName,
                        ColumnName: this.attribute.name
                    }
                });
                this.domain.keyvalues = result?.data?.map((item, index) => {
                    return {
                        id: index,
                        key: item,
                        value: item
                    };
                });
                if (this.domain.keyvalues.length === 0) {
                    this.showToast({
                        message: this.$t('noUniqueValuesFound'),
                        variant: 'info',
                        autoHideDelay: 5000
                    });
                }
            }
        }
    };
</script>

<style scoped>
.flexible-row {
    display: flex;
    width: 100%;
    margin-left: 0px;
}

.flexible-col {
    flex: 1;
    padding-left: 0px;
    padding-right: 0px;
}

.fixed-col {
    flex: 0 0 250px;
    padding-left: 0px;
}

.button-container {
    padding-left: 15px;
}

.generate-domain-panel {
    margin-bottom: 15px;
}

.delete {
    color: red;
    padding: 0.2em;
    justify-content: center;
}

#add-keyvalue {
    margin-top: 24px;
}
</style>
